import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Search, Users, Mail, Phone, CreditCard, User, ChevronRight, Plus, Link2, Copy, Check,
  ChevronLeft, UserPlus, Eye, MessageCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistributorClientsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [registrationToken, setRegistrationToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: '', cedula: '', gender: '',
    company: '', medical_insurance: '', height: '', weight: '', address: ''
  });

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchToken = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/distributor/registration-token`);
      setRegistrationToken(res.data.token);
    } catch (error) { console.error('Failed to fetch registration token:', error); }
  }, []);

  useEffect(() => { fetchClients(); fetchToken(); }, [fetchClients, fetchToken]);

  const registrationLink = `${window.location.origin}/register/${registrationToken}`;

  const copyLink = () => {
    navigator.clipboard.writeText(registrationLink);
    setCopied(true);
    toast.success('Link copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', phone: '', cedula: '', gender: '', company: '', medical_insurance: '', height: '', weight: '', address: '' });
    setCreateStep(0);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, { ...formData, user_type: 'client' });
      toast.success('Cliente creado exitosamente');
      setShowCreate(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cliente');
    }
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.display_id && c.display_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const stepTitles = ['Acceso', 'Personal', 'Adicional'];

  const canProceed = () => {
    if (createStep === 0) return formData.email && formData.password;
    if (createStep === 1) return formData.name && formData.gender;
    return true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in" data-testid="distributor-clients-page">
      {/* Header */}
      <div className="flex items-start justify-between pt-1">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Mis Clientes
          </h1>
          <p className="text-sm text-gray-400 mt-1 font-medium">{filteredClients.length} clientes registrados</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreate(true); }}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all duration-300"
          data-testid="create-client-btn"
        >
          <Plus size={16} />
          Nuevo
        </button>
      </div>

      {/* Registration Link Card */}
      {registrationToken && (
        <div className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm" data-testid="registration-link-card">
          <div className="h-1 bg-gradient-to-r from-blue-400 to-indigo-500" />
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Link2 size={16} className="text-blue-500" />
              <span className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Link de Registro
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Comparte este link con tus clientes para que se registren directamente vinculados a ti.
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2.5 text-xs text-gray-600 font-mono truncate border border-gray-100" data-testid="registration-link-url">
                {registrationLink}
              </div>
              <button
                onClick={copyLink}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-300 ${
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:shadow-lg shadow-blue-500/25'
                }`}
                data-testid="copy-link-btn"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          placeholder="Buscar cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-11 bg-white border-gray-100 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 rounded-xl h-11 shadow-sm"
          data-testid="search-clients"
        />
      </div>

      {/* Clients List */}
      {filteredClients.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users size={32} className="text-blue-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin clientes</p>
          <p className="text-sm text-gray-400 mt-1">Crea tu primer cliente o comparte tu link de registro</p>
          <button
            onClick={() => { resetForm(); setShowCreate(true); }}
            className="mt-5 flex items-center gap-2 mx-auto bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-blue-500/25"
          >
            <UserPlus size={16} />
            Crear mi primer cliente
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredClients.map((client, idx) => (
            <div
              key={client.id}
              className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
              style={{ animationDelay: `${idx * 60}ms` }}
              data-testid={`client-card-${client.id}`}
            >
              <div className="h-1 bg-gradient-to-r from-blue-400 to-indigo-500" />
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                    <span className="text-white font-bold text-sm">{client.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-gray-900 text-sm truncate">{client.name}</p>
                      {client.display_id && (
                        <span className="text-[10px] font-mono text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">{client.display_id}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Mail size={11} className="text-gray-400" />
                      <p className="text-xs text-gray-500 truncate">{client.email}</p>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <Phone size={11} className="text-gray-400" />
                        <p className="text-xs text-gray-500">{client.phone}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => navigate(`/distributor/clients/${client.id}`)}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl py-2 text-xs font-bold shadow-md shadow-blue-500/20 hover:shadow-lg transition-all"
                    data-testid={`view-profile-${client.id}`}
                  >
                    <Eye size={13} /> Ver Perfil
                  </button>
                  <button
                    onClick={() => navigate('/distributor/messages')}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl py-2 text-xs font-bold shadow-md shadow-teal-500/20 hover:shadow-lg transition-all"
                    data-testid={`message-btn-${client.id}`}
                  >
                    <MessageCircle size={13} /> Mensaje
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Client Modal (Wizard) */}
      <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-white border-0 rounded-3xl max-w-md" data-testid="create-client-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900 text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Nuevo Cliente
            </DialogTitle>
          </DialogHeader>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-2">
            {stepTitles.map((title, index) => (
              <div key={title} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                    index < createStep ? 'bg-blue-500 text-white' : index === createStep ? 'bg-blue-500 text-white ring-4 ring-blue-500/20' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {index < createStep ? <Check size={16} /> : index + 1}
                  </div>
                  <span className={`text-[10px] mt-1 font-medium ${index === createStep ? 'text-blue-500' : 'text-gray-400'}`}>{title}</span>
                </div>
                {index < 2 && <div className={`w-10 h-0.5 mx-1.5 rounded ${index < createStep ? 'bg-blue-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); createStep < 2 ? setCreateStep(createStep + 1) : handleCreate(e); }}>
            {createStep === 0 && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <h3 className="text-base font-semibold text-blue-600">Datos de Acceso</h3>
                  <p className="text-xs text-gray-500">Información para ingresar a la plataforma</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm">Correo Electrónico *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" size={16} />
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required placeholder="correo@ejemplo.com"
                      className="pl-10 bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 rounded-xl h-11"
                      data-testid="input-client-email" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm">Contraseña *</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required placeholder="Mínimo 8 caracteres"
                    className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 rounded-xl h-11"
                    data-testid="input-client-password" />
                </div>
              </div>
            )}

            {createStep === 1 && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <h3 className="text-base font-semibold text-blue-600">Información Personal</h3>
                  <p className="text-xs text-gray-500">Datos básicos del cliente</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm">Nombre Completo *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required placeholder="Nombre y apellido"
                    className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 rounded-xl h-11"
                    data-testid="input-client-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm">Género *</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                    <SelectTrigger className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 rounded-xl h-11" data-testid="select-client-gender">
                      <SelectValue placeholder="Seleccionar género" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-100 rounded-xl">
                      <SelectItem value="Masculino">Masculino</SelectItem>
                      <SelectItem value="Femenino">Femenino</SelectItem>
                      <SelectItem value="Prefiero no decir">Prefiero no decir</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-semibold text-sm">Teléfono</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1234567890"
                      className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 rounded-xl h-11"
                      data-testid="input-client-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-semibold text-sm">Cédula</Label>
                    <Input value={formData.cedula} onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                      placeholder="Número de ID"
                      className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 rounded-xl h-11"
                      data-testid="input-client-cedula" />
                  </div>
                </div>
              </div>
            )}

            {createStep === 2 && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <h3 className="text-base font-semibold text-blue-600">Información Adicional</h3>
                  <p className="text-xs text-gray-500">Datos complementarios (opcionales)</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-semibold text-sm">Empresa</Label>
                    <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Nombre" className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 rounded-xl h-11" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-semibold text-sm">Seguro Médico</Label>
                    <Input value={formData.medical_insurance} onChange={(e) => setFormData({ ...formData, medical_insurance: e.target.value })}
                      placeholder="Nombre" className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 rounded-xl h-11" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-semibold text-sm">Estatura (pies)</Label>
                    <Input value={formData.height} onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      placeholder="Ej: 5'8" className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 rounded-xl h-11" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-semibold text-sm">Peso</Label>
                    <Input value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      placeholder="Ej: 150 lbs" className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 rounded-xl h-11" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold text-sm">Dirección</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Dirección completa" className="bg-gray-50 border-transparent focus:bg-white focus:border-blue-400 rounded-xl h-11" />
                </div>
              </div>
            )}

            <div className="flex justify-between mt-6 pt-4 border-t border-gray-100">
              {createStep > 0 ? (
                <Button type="button" variant="outline" onClick={() => setCreateStep(createStep - 1)}
                  className="border-gray-200 text-gray-600 rounded-full">
                  <ChevronLeft size={16} className="mr-1" /> Anterior
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}
                  className="border-gray-200 text-gray-600 rounded-full">
                  Cancelar
                </Button>
              )}
              {createStep < 2 ? (
                <Button type="submit" disabled={!canProceed()}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full font-bold shadow-lg shadow-blue-500/25">
                  Siguiente <ChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button type="submit"
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full font-bold shadow-lg shadow-blue-500/25"
                  data-testid="save-client-btn">
                  <Check size={16} className="mr-1" /> Guardar
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DistributorClientsPage;
